const SSOUtils = (() => {
    /**
     * @typedef {string} SSOProvider A unique identifier of a know SSO provider
     */

    /**
     * @readonly
     * @enum {SSOProvider}
     * A list of know SSO provider identifiers
     */
    const PROVIDERS = Object.freeze({
        google: 'google_sso',
        microsoft: 'microsoft_sso'
    });

    /**
     * @readonly
     * @type {SSOProvider[]}
     * Preferred display order of providers
     */
    const DISPLAY_ORDER = Object.freeze([
        PROVIDERS.google,
        PROVIDERS.microsoft
    ]);

    /**
     * @typedef  {Object} SSOSupport
     * @property {SSOProvider} provider      Identifier of the SSO provider
     * @property {boolean}     globalSupport Whether the provider is supported at the global application level
     * @property {boolean}     tenantUse     Whether the provider has been discoverd in use on this client
     */

    /**
     * @private
     * @param {SSOProvider} provider
     * @param {boolean}     globalSupport
     * @param {boolean}     tenantUse
     * @returns {SSOSuport}
     */
    const newProviderSupport = (provider, globalSupport, tenantUse) => ({
        globalSupport,
        provider,
        tenantUse
    });

    /**
     * Determines which SSO providers are supported by a global application
     * @param {*} globalApp
     * @returns {Set<SSOProvider>}
     */
    const globalAppGetSupportedProviders = (globalApp) => {
        const result = [];

        if (globalApp.googleSSO || globalApp.googleSignIn) {
            result.push(PROVIDERS.google);
        }
        if (globalApp.azureSignIn || globalApp.microsoftSSO) {
            result.push(PROVIDERS.microsoft);
        }

        return new Set(result);
    };

    /**
     * Determines if a tenantApp has evidence of a specified SSO provider being used
     * @param {*}           tenantApp The tenant application object to be checked
     * @param {SSOProvider} provider  The SSO provider to be checked for usage evidence
     * @returns {boolean} `true` if there is evidence of the provider being used, `false` otherwise
     * @throws Throws an error if an unknown provider identifier is passed in
     */
    const hasTenantAppUsedProvider = (tenantApp, provider) => {
        if (provider === PROVIDERS.google) {
            return !!(
                tenantApp?.googleSSODetected ||
                tenantApp?.googleSignInDetected ||
                tenantApp?.googleSSO > 0 ||
                tenantApp?.googleSignInCount > 0
            );
        }

        if (provider === PROVIDERS.microsoft) {
            return !!(
                tenantApp?.azureSignInDetected ||
                tenantApp?.microsoftSSODetected ||
                tenantApp?.azureSignInCount > 0 ||
                tenantApp?.microsoftSSO > 0
            );
        }

        throw new Error('unknown sso provider specified');
    };

    /**
     * Determines which SSO providers are supported and used on a tenant application
     * @param {*} tenantApp
     * @returns {SSOSupport[]} A list of objects indicating what level of support is available by providers
     * @throws Will throw an error if the tenantApp does not have an `application` property containing the globalApp object
     */
    const tenantAppGetSSOSupport = (tenantApp) => {
        if (!tenantApp?.application) {
            throw new Error('no global application');
        }

        // collect providers which are known to be supported by the application at a global level
        const globalSupportProviders = globalAppGetSupportedProviders(
            tenantApp.application
        );

        return (
            Object.values(PROVIDERS)
                // iterate over all known providers and determine if the app has support or use of the provider
                // NOTE: we need to iterate over all known providers to catch the edge case where we may not have detected
                //       support at a global app level yet, but already have usage at the tenant app level for a provider
                .map((provider) => {
                    const globalSupport = globalSupportProviders.has(provider);
                    const tenantUse = hasTenantAppUsedProvider(
                        tenantApp,
                        provider
                    );

                    if (globalSupport || tenantUse) {
                        return newProviderSupport(
                            provider,
                            globalSupport,
                            tenantUse
                        );
                    }
                    return null;
                })
                // remove any falsy (null) entries to reduce the array to only providers which have some support or use
                .filter((el) => !!el)
        );
    };

    return {
        DISPLAY_ORDER,
        globalAppGetSupportedProviders,
        hasTenantAppUsedProvider,
        PROVIDERS,
        tenantAppGetSSOSupport
    };
})();

export default SSOUtils;
